import {DeepReadonly} from 'ts-essentials'
import {ObjUtil, Util} from 'yougile-util'
import {
  BoardHubData,
  ColumnHubData,
  CompanyHubData,
  DataFieldValues,
  DealHubData,
  ExtensionHubData,
  isDataObjectId,
  NotificationSettings,
  ProjectHubData,
  TaskHubData
} from 'yougile-model'
import {Chat, ChatJSON} from '../chat'
import {DataObjectId} from '../data-object'
import {Department} from '../departments'
import {JSONLikeValue} from '../diff'
import {Entity, EntityJSON} from '../entity'
import {HubUtil} from '../hub-util'

export type HubType =
  | 'task'
  | 'column'
  | 'board'
  | 'project'
  | 'company'
  | 'other'

type AnyType<T> = Omit<T, 'type'> & {
  type?: HubType
}

export type HubData = {
  seenNews?: DataObjectId[]
  closedNews?: DataObjectId[]

  notificationSettings?: NotificationSettings
  markImportantMap?: {[id: DataObjectId]: number | undefined}
  chatSettings?: {
    showReadSystemMessages: boolean
  }

  companyLinkSettings?: {
    [companyId: DataObjectId]: {newRegistrationsSince?: number}
  }

  // hosted in profile under the same id as company
  companySettings?: CompanySettings

  // hosted in hubs inside .departments container
  department?: Department

  updatedByScript?: number

  fieldValues?: DataFieldValues
} & AnyType<CompanyHubData> &
  AnyType<ProjectHubData> &
  AnyType<BoardHubData> &
  AnyType<ColumnHubData> &
  AnyType<TaskHubData> &
  AnyType<ExtensionHubData> &
  AnyType<DealHubData>

export interface HubJSON extends EntityJSON {
  isLazy?: boolean
  chat?: ChatJSON
  connectedHubs?: {[key: string]: number}
  data?: HubData
  stickers?: {[id: string]: JSONLikeValue}
  complete?: number // archivation timestamp
}

export interface HubLike {
  companyId: DataObjectId
  id: DataObjectId
}

export interface EntitledHubLike extends HubLike {
  title: string
}

export class Hub extends Entity implements HubLike {
  chat: Chat
  data: HubData = {}
  complete: number | undefined
  stickers: {[id: string]: JSONLikeValue} = {}
  private connectedHubs?: Set<DataObjectId>

  constructor(companyId: DataObjectId, title: string, canBeLazy = false) {
    super(companyId, title)
    this.dataType = 'Hub'
    this.chat = new Chat(companyId)
    if (!canBeLazy) {
      this.isLazy = false
    }
  }

  private _locHistory?: {[locationId: string]: number[]}

  get locHistory(): {[locationId: string]: number[]} {
    if (!this._locHistory && !this.isLazy) {
      this._locHistory = {}
    }
    return this._locHistory || {}
  }

  set locHistory(value: {[locationId: string]: number[]}) {
    if (this.isLazy && ObjUtil.isEmpty(value)) {
      delete this._locHistory
      return
    }
    this._locHistory = value
  }

  static connect(hub1: Hub, hub2: Hub): void {
    if (Hub.isConnected(hub1, hub2)) {
      throw new Error('Already connected')
    }
    if (!hub1.connectedHubs) {
      hub1.initConnectedHubs()
    }
    hub1.connectedHubs?.add(hub2.id)
    if (!hub2.connectedHubs) {
      hub2.initConnectedHubs()
    }
    hub2.connectedHubs?.add(hub1.id)
    hub1.commit()
    hub2.commit()
  }

  static isConnected(hub1: Hub, hub2: Hub): boolean {
    if (!hub1.connectedHubs || !hub2.connectedHubs) {
      return false
    }
    if (hub1.connectedHubs.has(hub2.id) !== hub2.connectedHubs.has(hub1.id)) {
      throw new Error('Inconsistent connections')
    }
    return hub1.connectedHubs.has(hub2.id)
  }

  static disconnect(hub1: Hub, hub2: Hub): void {
    if (!Hub.isConnected(hub1, hub2)) {
      throw new Error('Not connected')
    }
    if (hub1.connectedHubs) {
      hub1.connectedHubs.delete(hub2.id)
    }
    if (hub2.connectedHubs) {
      hub2.connectedHubs.delete(hub1.id)
    }
    hub1.commit()
    hub2.commit()
  }

  initConnectedHubs(): void {
    this.connectedHubs = new Set()
  }

  getLocHistory(): DeepReadonly<{[locationId: string]: number[]}> {
    return this.locHistory
  }

  cleanLocHistory(): void {
    this.locHistory = {}
  }

  listConnectedHubs(): DataObjectId[] {
    if (!this.connectedHubs) {
      return []
    }
    return [...this.connectedHubs]
  }

  public unload(): void {
    const lazyJSON = HubUtil.makeLazyTaskJSON(this.toJSON())
    this.fromJSON(this.companyId, lazyJSON)
    this.isLazy = true
  }

  override toJSON(): HubJSON {
    const res = super.toJSON() as HubJSON

    if (this.chat.messages.size > 0) {
      res.chat = this.chat.toJSON()
    }
    if (!ObjUtil.isEmpty(this.data)) {
      res.data = Util.clone(this.data)
    }
    if (!ObjUtil.isEmpty(this.stickers)) {
      res.stickers = Util.clone(this.stickers)
    }
    if (!ObjUtil.isEmpty(this.locHistory)) {
      res.locHistory = Util.clone(this.locHistory)
    }
    if (this.complete) {
      res.complete = this.complete
    }
    if (this.connectedHubs && this.connectedHubs.size > 0) {
      const connHubs: {[key: string]: number} = {}
      res.connectedHubs = connHubs
      this.connectedHubs.forEach((hid: DataObjectId) => {
        connHubs[hid] = 1
      })
    }
    return res
  }

  override fromJSON(companyId: DataObjectId, data: HubJSON): Hub {
    super.fromJSON(companyId, data)
    if (data.isLazy) {
      this.isLazy = true
    }
    if (data.chat) {
      this.chat.fromJSON(companyId, data.chat)
    } else {
      this.chat.companyId = this.companyId
    }
    if (data.connectedHubs) {
      this.connectedHubs = new Set(
        Object.keys(data.connectedHubs) as DataObjectId[]
      )
    }
    if (data.data) {
      this.data = data.data ? Util.clone(data.data) : {}
    }
    this.stickers = data.stickers ? Util.clone(data.stickers) : {}
    this.locHistory = data.locHistory ? Util.clone(data.locHistory) : {}
    if (data.complete) {
      this.complete = data.complete
    } else {
      delete this.complete
    }
    return this
  }
}

export const isTaskWithoutPermissions = (hub: Hub | HubJSON | undefined) =>
  !!hub?.deleted && hub.title === hub.id && hub.properties?.del === 'no'

export const isDeletedEntity = (ent: Entity | EntityJSON | undefined) =>
  !!ent?.deleted && !(ent.title === ent.id && ent.properties?.del === 'no')

export const isHub = (item: unknown): item is Hub => {
  return item instanceof Hub
}

/** Settings inside user’s profile */
export type TaskListType = 'private' | 'custom'

export interface TasksList {
  title: string
  type?: TaskListType
  tasks?: DataObjectId[]
  createdByDefault?: boolean
}

export interface TasksLists {
  [key: TasksListId]: TasksList
}

// string should become -> 'current-tasks' | 'done-tasks' | DataObjectId
export type TasksListId = string | DataObjectId

export function isTasksListId(listId: unknown): listId is TasksListId {
  if (typeof listId !== 'string') {
    return false
  }

  if (listId === 'current-tasks' || listId === 'done-tasks') {
    return true
  }

  return isDataObjectId(listId)
}

export interface CompanySettingsOrder {
  [key: TasksListId]: DataObjectId[]
}

export interface CompanySettings {
  stars: {
    [id: string]: 1
  }
  order: CompanySettingsOrder
  orderChange: {
    [key: string]: number
  }
  tasksLists?: TasksLists
  tasksListsOrder?: TasksListId[]
  isDefaultPrivateListCreated?: boolean
}
